import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import ExportPanel from './ExportPanel';
import CustomerProfile from './CustomerProfile';
import CreateAppointmentModal from './CreateAppointmentModal';

const API = process.env.REACT_APP_API_URL || '/api';
const PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50, 100, 200, 500, 1000, 2000];

export default function AppointmentsTab({ noteModal, setNoteModal, highlightedAppointmentId, t }) {
  // Customer profile modal
  const [profileCustomerId, setProfileCustomerId] = useState(null);
  // Create appointment modal
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Data state
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // Search state
  const [apptSearch, setApptSearch] = useState('');
  const [apptSearchDebounced, setApptSearchDebounced] = useState('');
  const apptSearchTimer = useRef(null);

  // Structured filters
  const [apptFilterStatus, setApptFilterStatus] = useState('');
  const [apptFilterService, setApptFilterService] = useState('');
  const [apptFilterDateFrom, setApptFilterDateFrom] = useState('');
  const [apptFilterDateTo, setApptFilterDateTo] = useState('');
  const [apptFiltersExpanded, setApptFiltersExpanded] = useState(false);

  // Sorting
  const [apptSortCol, setApptSortCol] = useState('date');
  const [apptSortDir, setApptSortDir] = useState('asc');

  // Reminder scheduling modal state
  const [reminderModal, setReminderModal] = useState(null); // { appt, newStatus }
  const [reminderEnabled, setReminderEnabled] = useState(false);
  const [reminderInterval, setReminderInterval] = useState(6);
  const [reminderCustomDate, setReminderCustomDate] = useState('');
  const [reminderEmail, setReminderEmail] = useState(true);
  const [reminderSms, setReminderSms] = useState(false);
  const [reminderRule, setReminderRule] = useState(null);
  const [reminderSaving, setReminderSaving] = useState(false);

  // Actual price inputs for completion
  const [servicePrices, setServicePrices] = useState({});

  const rm = t.admin?.reminders || {};
  const pg = t.admin?.search || {};

  // --- Data loading ---
  const loadAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: pageSize };
      if (apptSearchDebounced) params.q = apptSearchDebounced;
      if (apptFilterStatus) params.status = apptFilterStatus;
      if (apptFilterService) params.service = apptFilterService;
      if (apptFilterDateFrom) params.date_from = apptFilterDateFrom;
      if (apptFilterDateTo) params.date_to = apptFilterDateTo;
      if (apptSortCol) params.sort = apptSortCol;
      if (apptSortDir) params.order = apptSortDir;

      const res = await axios.get(`${API}/admin/appointments`, { params });
      const data = res.data;
      setAppointments(data.data || []);
      if (data.pagination) {
        setTotalPages(data.pagination.totalPages || 1);
        setTotal(data.pagination.total || 0);
      }
    } catch (err) {
      console.error('Failed to load appointments:', err.message);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, apptSearchDebounced, apptFilterStatus, apptFilterService, apptFilterDateFrom, apptFilterDateTo, apptSortCol, apptSortDir]);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  // --- Self-contained status update ---
  const updateAppointmentStatus = async (id, status, extraPayload = {}) => {
    try {
      await axios.patch(`${API}/admin/appointments/${id}`, { status, ...extraPayload });
      loadAppointments();
    } catch (err) {
      alert(t.admin.errorUpdate);
    }
  };

  // --- Reminder logic ---
  const handleStatusChange = (apptId, newStatus) => {
    if (newStatus === 'completed') {
      const appt = appointments.find(a => a.id === apptId);
      if (appt && appt.status !== 'completed') {
        setReminderModal({ appt, newStatus });
        setReminderEnabled(false);
        setReminderInterval(6);
        setReminderCustomDate('');
        setReminderEmail(true);
        setReminderSms(false);
        // Initialize actual prices from services
        const prices = {};
        if (Array.isArray(appt.services)) {
          appt.services.forEach(s => {
            prices[s.id] = s.actual_price != null ? String(s.actual_price) : '';
          });
        }
        setServicePrices(prices);
        // Fetch rule for this service
        if (appt.service_id) {
          axios.get(`${API}/admin/reminders/rules/service/${appt.service_id}`)
            .then(res => {
              const rule = res.data;
              setReminderRule(rule);
              if (rule && rule.enabled) {
                setReminderEnabled(true);
                const intervals = rule.default_intervals_months || [6];
                setReminderInterval(intervals[0] || 6);
                setReminderEmail(rule.default_channel_email !== false);
                setReminderSms(!!rule.default_channel_sms);
              }
            })
            .catch(() => setReminderRule(null));
        }
        return;
      }
    }
    updateAppointmentStatus(apptId, newStatus);
  };

  const buildServicePricesPayload = () => {
    return Object.entries(servicePrices)
      .filter(([, price]) => price !== '' && !isNaN(price))
      .map(([serviceId, price]) => ({ service_id: Number(serviceId), actual_price: Number(price) }));
  };

  const handleReminderConfirm = async () => {
    if (!reminderModal) return;
    const { appt, newStatus } = reminderModal;

    setReminderSaving(true);
    try {
      // First update the appointment status with actual prices
      await updateAppointmentStatus(appt.id, newStatus, { service_prices: buildServicePricesPayload() });

      // Then create the reminder if enabled
      if (reminderEnabled) {
        let remindAt;
        if (reminderCustomDate) {
          remindAt = new Date(reminderCustomDate + 'T09:00:00').toISOString();
        } else {
          const date = new Date();
          date.setMonth(date.getMonth() + reminderInterval);
          date.setHours(9, 0, 0, 0);
          remindAt = date.toISOString();
        }
        await axios.post(`${API}/admin/reminders`, {
          customer_id: appt.user_id,
          appointment_id: appt.id,
          service_id: appt.service_id,
          vehicle_id: appt.vehicle_id || null,
          remind_at: remindAt,
          channel_email: reminderEmail,
          channel_sms: reminderSms
        });
      }
    } catch (err) {
      console.error('Reminder scheduling error:', err);
    } finally {
      setReminderSaving(false);
      setReminderModal(null);
    }
  };

  const handleReminderSkip = () => {
    if (!reminderModal) return;
    updateAppointmentStatus(reminderModal.appt.id, reminderModal.newStatus, { service_prices: buildServicePricesPayload() });
    setReminderModal(null);
  };

  // --- Search debounce ---
  const handleApptSearch = (val) => {
    setApptSearch(val);
    clearTimeout(apptSearchTimer.current);
    apptSearchTimer.current = setTimeout(() => {
      setApptSearchDebounced(val);
      setPage(1);
    }, 300);
  };

  // --- Sorting ---
  const toggleSort = (col) => {
    if (apptSortCol === col) {
      setApptSortDir(apptSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setApptSortCol(col);
      setApptSortDir('asc');
    }
    setPage(1);
  };

  const sortIcon = (col) => {
    if (apptSortCol !== col) return <span style={{ color: '#475569', marginLeft: '0.25rem' }}>{'\u21C5'}</span>;
    return <span style={{ color: '#D42B2B', marginLeft: '0.25rem' }}>{apptSortDir === 'asc' ? '\u25B2' : '\u25BC'}</span>;
  };

  const thStyle = { cursor: 'pointer', userSelect: 'none' };

  // --- Filter helpers ---
  const activeFilterCount = [apptFilterStatus, apptFilterService, apptFilterDateFrom, apptFilterDateTo].filter(Boolean).length;
  const hasApptFilters = apptSearchDebounced || apptFilterStatus || apptFilterService || apptFilterDateFrom || apptFilterDateTo;

  const clearApptFilters = () => {
    setApptSearch(''); setApptSearchDebounced('');
    setApptFilterStatus(''); setApptFilterService('');
    setApptFilterDateFrom(''); setApptFilterDateTo('');
    setPage(1);
  };

  // --- Page size ---
  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(1);
  };

  // --- Formatting helpers ---
  const formatDate = (date) => {
    if (!date) return '';
    const str = typeof date === 'string' ? date : String(date);
    const dateOnly = str.substring(0, 10);
    return new Date(dateOnly + 'T12:00:00').toLocaleDateString();
  };
  const formatTime = (time) => time?.substring(0, 5);

  // --- Pagination display ---
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h2 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '2rem', color: '#D42B2B', margin: 0 }}>{t.admin.allAppointments}</h2>
        <button className="btn btn-primary btn-sm" onClick={() => setShowCreateModal(true)} style={{ fontSize: '0.8rem' }}>
          + {t.admin?.createAppointment?.title || 'New Appointment'}
        </button>
      </div>
      <ExportPanel />
      {/* Search + filter bar */}
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 280px', maxWidth: '400px' }}>
          <input
            type="text"
            className="form-control"
            placeholder={pg.placeholder || 'Search by name, phone, email, vehicle\u2026'}
            value={apptSearch}
            onChange={e => handleApptSearch(e.target.value)}
            style={{ paddingRight: '2.5rem' }}
          />
          {apptSearch && (
            <button
              onClick={() => { setApptSearch(''); setApptSearchDebounced(''); setPage(1); }}
              aria-label={t.common.close}
              style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#94A3B8', fontSize: '1.1rem', cursor: 'pointer', padding: '0.25rem' }}
            >&times;</button>
          )}
        </div>
        <button
          className={`btn btn-sm ${apptFiltersExpanded ? 'btn-outline-primary' : 'btn-outline-secondary'}`}
          onClick={() => setApptFiltersExpanded(!apptFiltersExpanded)}
          style={{ whiteSpace: 'nowrap' }}
        >
          {pg.filters || 'Filters'}{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          {apptFiltersExpanded ? ' \u25B2' : ' \u25BC'}
        </button>
        {hasApptFilters && (
          <button className="btn btn-sm btn-outline-danger" onClick={clearApptFilters} style={{ whiteSpace: 'nowrap' }}>
            {pg.clearAll || 'Clear all'}
          </button>
        )}
        {!loading && total > 0 && (
          <span style={{ fontSize: '0.85rem', color: '#94A3B8', marginLeft: 'auto' }}>
            {(pg.showing || '{from}\u2013{to} of {total}').replace('{from}', from).replace('{to}', to).replace('{total}', total)}
          </span>
        )}
      </div>
      {/* Expanded filter panel */}
      {apptFiltersExpanded && (
        <div style={{ background: '#111827', border: '1px solid rgba(148,163,184,0.15)', padding: '1rem', marginBottom: '1rem' }}>
          <div className="row g-2">
            <div className="col-12 col-sm-6 col-md-4 col-lg-3">
              <label style={{ fontSize: '0.75rem', color: '#94A3B8', marginBottom: '0.25rem', display: 'block' }}>{t.admin.tableHeaders.status}</label>
              <select className="form-select form-select-sm" value={apptFilterStatus} onChange={e => { setApptFilterStatus(e.target.value); setPage(1); }}>
                <option value="">{pg.allStatuses || 'All statuses'}</option>
                <option value="pending">{t.dashboard.status.pending}</option>
                <option value="pending_payment">{t.booking?.paymentPending || 'Payment Pending'}</option>
                <option value="confirmed">{t.dashboard.status.confirmed}</option>
                <option value="in_progress">{t.dashboard.status.in_progress}</option>
                <option value="completed">{t.dashboard.status.completed}</option>
                <option value="cancelled">{t.dashboard.status.cancelled}</option>
              </select>
            </div>
            <div className="col-12 col-sm-6 col-md-4 col-lg-3">
              <label style={{ fontSize: '0.75rem', color: '#94A3B8', marginBottom: '0.25rem', display: 'block' }}>{t.admin.tableHeaders.service}</label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder={pg.servicePlaceholder || 'Filter by service\u2026'}
                value={apptFilterService}
                onChange={e => { setApptFilterService(e.target.value); setPage(1); }}
              />
            </div>
            <div className="col-6 col-sm-3 col-md-2 col-lg-1-5" style={{ flex: '0 0 auto', width: 'auto', minWidth: '140px' }}>
              <label style={{ fontSize: '0.75rem', color: '#94A3B8', marginBottom: '0.25rem', display: 'block' }}>{pg.dateFrom || 'From'}</label>
              <input type="date" className="form-control form-control-sm" value={apptFilterDateFrom} onChange={e => { setApptFilterDateFrom(e.target.value); setPage(1); }} />
            </div>
            <div className="col-6 col-sm-3 col-md-2 col-lg-1-5" style={{ flex: '0 0 auto', width: 'auto', minWidth: '140px' }}>
              <label style={{ fontSize: '0.75rem', color: '#94A3B8', marginBottom: '0.25rem', display: 'block' }}>{pg.dateTo || 'To'}</label>
              <input type="date" className="form-control form-control-sm" value={apptFilterDateTo} onChange={e => { setApptFilterDateTo(e.target.value); setPage(1); }} />
            </div>
          </div>
        </div>
      )}
      {/* Table with loading overlay */}
      <div className="table-responsive" style={{ position: 'relative', minHeight: '200px' }}>
        {loading && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(12,18,32,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}>
            <span style={{ color: '#D42B2B', fontFamily: "'Rajdhani', sans-serif" }}>{t.common?.loading || 'Loading...'}</span>
          </div>
        )}
        <table className="table table-dark table-hover">
          <thead>
          <tr>
            <th style={thStyle} onClick={() => toggleSort('customer')}>{t.admin.tableHeaders.customer} {sortIcon('customer')}</th>
            <th style={thStyle} onClick={() => toggleSort('service')}>{t.admin.tableHeaders.service} {sortIcon('service')}</th>
            <th style={thStyle} onClick={() => toggleSort('vehicle')}>{t.admin.tableHeaders.vehicle} {sortIcon('vehicle')}</th>
            <th style={thStyle} onClick={() => toggleSort('date')}>{t.admin.tableHeaders.date} {sortIcon('date')}</th>
            <th style={thStyle} onClick={() => toggleSort('time')}>{t.admin.tableHeaders.time} {sortIcon('time')}</th>
            <th style={thStyle} onClick={() => toggleSort('status')}>{t.admin.tableHeaders.status} {sortIcon('status')}</th>
            <th>{t.admin.tableHeaders.actions}</th>
          </tr>
          </thead>
          <tbody>
          {appointments.map(appt => (
              <tr key={appt.id} id={`appt-${appt.id}`} className={highlightedAppointmentId === appt.id ? 'appointment-highlight' : ''}>
                <td><span onClick={() => setProfileCustomerId(appt.user_id)} style={{ color: '#E8ECF0', cursor: 'pointer', borderBottom: '1px dashed rgba(148,163,184,0.3)' }} title={t.admin?.customerProfile?.viewProfile || 'View profile'}>{appt.first_name} {appt.last_name}</span></td>
                <td>{Array.isArray(appt.services) && appt.services.length > 0
                  ? appt.services.map(s => s.name_fr || s.name).join(' + ')
                  : (appt.service_name_fr || appt.service_name)}</td>
                <td style={{ fontSize: '0.85rem' }}>{appt.year} {appt.make} {appt.model}</td>
                <td>{formatDate(appt.appointment_date)}</td>
                <td>{formatTime(appt.appointment_time)}</td>
                <td>
                  <select
                      className="form-select form-select-sm"
                      value={appt.status}
                      onChange={(e) => handleStatusChange(appt.id, e.target.value)}
                      style={{ width: 'auto', fontSize: '0.8rem' }}
                  >
                    <option value="pending">{t.dashboard.status.pending}</option>
                    <option value="pending_payment">{t.booking?.paymentPending || 'Payment Pending'}</option>
                    <option value="confirmed">{t.dashboard.status.confirmed}</option>
                    <option value="in_progress">{t.dashboard.status.in_progress}</option>
                    <option value="completed">{t.dashboard.status.completed}</option>
                    <option value="cancelled">{t.dashboard.status.cancelled}</option>
                  </select>
                  {appt.payment_status && (
                    <span style={{ display: 'inline-block', marginLeft: '0.5rem', fontSize: '0.65rem', padding: '0.15rem 0.5rem', borderRadius: '3px',
                      background: appt.payment_status === 'completed' ? 'rgba(34,197,94,0.15)' : appt.payment_status === 'refunded' ? 'rgba(234,179,8,0.15)' : 'rgba(148,163,184,0.15)',
                      color: appt.payment_status === 'completed' ? '#22c55e' : appt.payment_status === 'refunded' ? '#eab308' : '#94A3B8',
                    }}>
                      {appt.payment_status === 'completed' ? (t.admin?.paymentPaid || 'Paid') : appt.payment_status === 'refunded' ? (t.admin?.paymentRefunded || 'Refunded') : (t.admin?.paymentPending || 'Pending')}
                      {appt.payment_amount_cents ? ` $${(appt.payment_amount_cents / 100).toFixed(2)}` : ''}
                    </span>
                  )}
                </td>
                <td>
                  {appt.notes && (
                      <button className="btn btn-outline-primary btn-sm" onClick={() => setNoteModal({ show: true, notes: appt.notes, customer: `${appt.first_name} ${appt.last_name}` })} style={{ fontSize: '0.7rem' }}>
                        {t.admin.viewNotes}
                      </button>
                  )}
                </td>
              </tr>
          ))}
          {!loading && appointments.length === 0 && (
            <tr><td colSpan="7" style={{ textAlign: 'center', padding: '2rem', color: '#94A3B8' }}>{pg.noResults || 'No results found'}</td></tr>
          )}
          </tbody>
        </table>
      </div>

      {/* Pagination controls */}
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '1rem', flexWrap: 'wrap' }}>
        {totalPages > 1 && (
          <button
            className="btn btn-outline-secondary btn-sm"
            disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}
          >
            {pg.prev || '\u2039 Previous'}
          </button>
        )}
        {totalPages > 1 && (
          <span style={{ color: '#94A3B8', fontSize: '0.9rem' }}>
            Page {page} / {totalPages}
          </span>
        )}
        {totalPages > 1 && (
          <button
            className="btn btn-outline-secondary btn-sm"
            disabled={page >= totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            {pg.next || 'Next \u203A'}
          </button>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <label style={{ color: '#94A3B8', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{pg.rowsPerPage || 'Rows:'}</label>
          <select
            className="form-select form-select-sm"
            value={pageSize}
            onChange={e => handlePageSizeChange(Number(e.target.value))}
            style={{ width: 'auto', minWidth: '70px' }}
          >
            {PAGE_SIZE_OPTIONS.map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Reminder Scheduling Modal */}
      {reminderModal && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }} onClick={() => setReminderModal(null)}>
          <div className="modal-dialog" onClick={e => e.stopPropagation()}>
            <div className="modal-content" style={{ background: '#0C1220', border: '1px solid rgba(148,163,184,0.2)', color: '#E8ECF0' }}>
              <div className="modal-header" style={{ borderBottom: '1px solid rgba(148,163,184,0.15)' }}>
                <h5 className="modal-title" style={{ fontFamily: "'Rajdhani', sans-serif", color: '#D42B2B' }}>
                  {rm.scheduleTitle || 'Complete Appointment'}
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setReminderModal(null)}></button>
              </div>
              <div className="modal-body" style={{ padding: '1.5rem' }}>
                <div style={{ marginBottom: '1rem', fontSize: '0.9rem', color: '#94A3B8' }}>
                  {reminderModal.appt.first_name} {reminderModal.appt.last_name} — {Array.isArray(reminderModal.appt.services) && reminderModal.appt.services.length > 0
                    ? reminderModal.appt.services.map(s => s.name_fr || s.name).join(' + ')
                    : (reminderModal.appt.service_name_fr || reminderModal.appt.service_name)}
                </div>

                {/* Actual prices */}
                {Array.isArray(reminderModal.appt.services) && reminderModal.appt.services.length > 0 && (
                  <div style={{ background: '#111827', border: '1px solid rgba(148,163,184,0.15)', padding: '1.25rem', marginBottom: '1rem' }}>
                    <label style={{ fontSize: '0.75rem', color: '#D42B2B', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: '0.75rem' }}>
                      {t.admin?.reports?.actualPrices || 'Actual Prices Charged'}
                    </label>
                    {reminderModal.appt.services.map(s => (
                      <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                        <span style={{ flex: 1, fontSize: '0.85rem', color: '#E8ECF0' }}>{s.name_fr || s.name}</span>
                        <span style={{ fontSize: '0.7rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                          ${Number(s.price_min).toFixed(2)} – ${Number(s.price_max).toFixed(2)}
                        </span>
                        <div style={{ position: 'relative', width: '110px' }}>
                          <span style={{ position: 'absolute', left: '0.5rem', top: '50%', transform: 'translateY(-50%)', color: '#94A3B8', fontSize: '0.85rem' }}>$</span>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            step="0.01"
                            min="0"
                            placeholder={((Number(s.price_min) + Number(s.price_max)) / 2).toFixed(0)}
                            value={servicePrices[s.id] || ''}
                            onChange={e => setServicePrices(prev => ({ ...prev, [s.id]: e.target.value }))}
                            style={{ paddingLeft: '1.25rem', width: '110px' }}
                          />
                        </div>
                      </div>
                    ))}
                    <div style={{ fontSize: '0.7rem', color: '#64748B', marginTop: '0.5rem' }}>
                      {t.admin?.reports?.priceNote || 'Optional — leave blank to use estimates in reports'}
                    </div>
                  </div>
                )}

                {/* Toggle reminder */}
                <div className="form-check form-switch" style={{ marginBottom: '1rem' }}>
                  <input className="form-check-input" type="checkbox" checked={reminderEnabled} onChange={e => setReminderEnabled(e.target.checked)} id="reminderToggle" />
                  <label className="form-check-label" htmlFor="reminderToggle" style={{ fontSize: '0.9rem' }}>
                    {rm.scheduleReminder || 'Schedule a maintenance reminder'}
                  </label>
                </div>

                {reminderEnabled && (
                  <div style={{ background: '#111827', border: '1px solid rgba(148,163,184,0.15)', padding: '1.25rem', marginBottom: '1rem' }}>
                    {/* Interval selection */}
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ fontSize: '0.75rem', color: '#94A3B8', display: 'block', marginBottom: '0.25rem' }}>
                        {rm.interval || 'Remind in'}
                      </label>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                        {(reminderRule?.default_intervals_months || [3, 6, 12]).map(m => (
                          <button
                            key={m}
                            className={`btn btn-sm ${reminderInterval === m && !reminderCustomDate ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => { setReminderInterval(m); setReminderCustomDate(''); }}
                            style={{ fontSize: '0.8rem' }}
                          >
                            {m} {rm.months || 'months'}
                          </button>
                        ))}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                        <label style={{ fontSize: '0.75rem', color: '#94A3B8', whiteSpace: 'nowrap' }}>{rm.customDate || 'Custom date'}:</label>
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          value={reminderCustomDate}
                          onChange={e => setReminderCustomDate(e.target.value)}
                          min={new Date().toISOString().split('T')[0]}
                          style={{ width: 'auto' }}
                        />
                      </div>
                    </div>

                    {/* Channels */}
                    <div>
                      <label style={{ fontSize: '0.75rem', color: '#94A3B8', display: 'block', marginBottom: '0.25rem' }}>
                        {rm.channels || 'Channels'}
                      </label>
                      <div style={{ display: 'flex', gap: '1.5rem' }}>
                        <div className="form-check">
                          <input className="form-check-input" type="checkbox" checked={reminderEmail} onChange={e => setReminderEmail(e.target.checked)} id="chEmail" />
                          <label className="form-check-label" htmlFor="chEmail" style={{ fontSize: '0.85rem' }}>Email</label>
                        </div>
                        <div className="form-check">
                          <input className="form-check-input" type="checkbox" checked={reminderSms} onChange={e => setReminderSms(e.target.checked)} id="chSms" />
                          <label className="form-check-label" htmlFor="chSms" style={{ fontSize: '0.85rem' }}>SMS</label>
                        </div>
                      </div>
                    </div>

                    {/* Preview */}
                    <div style={{ marginTop: '1rem', fontSize: '0.8rem', color: '#64748B' }}>
                      {rm.willSend || 'Reminder will be sent on'}: <strong style={{ color: '#E8ECF0' }}>
                        {reminderCustomDate
                          ? new Date(reminderCustomDate + 'T12:00:00').toLocaleDateString()
                          : (() => { const d = new Date(); d.setMonth(d.getMonth() + reminderInterval); return d.toLocaleDateString(); })()
                        }
                      </strong>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer" style={{ borderTop: '1px solid rgba(148,163,184,0.15)', display: 'flex', justifyContent: 'space-between' }}>
                <button className="btn btn-outline-secondary btn-sm" onClick={handleReminderSkip} disabled={reminderSaving}>
                  {rm.skipReminder || 'Complete without reminder'}
                </button>
                <button className="btn btn-primary btn-sm" onClick={handleReminderConfirm} disabled={reminderSaving}>
                  {reminderSaving ? (t.admin.sending || 'Saving...') : reminderEnabled ? (rm.completeAndSchedule || 'Complete & Schedule') : (rm.completeOnly || 'Complete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {profileCustomerId && (
        <CustomerProfile customerId={profileCustomerId} onClose={() => setProfileCustomerId(null)} t={t} />
      )}

      {showCreateModal && (
        <CreateAppointmentModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => loadAppointments()}
          t={t}
        />
      )}
    </div>
  );
}
